"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Menu, X } from "lucide-react"
import { motion } from "framer-motion"
import { ThemeToggle } from "./theme-toggle"
import { LanguageToggle } from "./language-toggle"
import { useLanguage } from "@/lib/i18n"

export function Navigation() {
  const { t } = useLanguage()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)

  const navLinks = [
    { href: "#work", label: t("nav.work") },
    { href: "#approach", label: t("nav.approach") },
    { href: "#about", label: t("nav.about") },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
      const windowHeight =
        document.documentElement.scrollHeight - document.documentElement.clientHeight
      const scrolled = (window.scrollY / windowHeight) * 100
      setScrollProgress(scrolled)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isMobileMenuOpen])

  return (
    <>
      {/* Scroll progress bar - Gold to Teal gradient */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-0.5 z-[60] origin-left"
        style={{
          background: "linear-gradient(to right, #C9A227, #2A9D8F)",
          scaleX: scrollProgress / 100,
          opacity: scrollProgress > 5 ? 0.9 : 0,
        }}
        transition={{ opacity: { duration: 0.3 } }}
      />

      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? "glass-scrolled border-b border-white/10" : "bg-transparent"
        }`}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 xl:px-24">
          <div className="flex items-center justify-between h-[var(--nav-height)] md:h-[var(--nav-height-desktop)]">
            {/* Logo with subtle glow on hover */}
            <Link
              href="/"
              className="relative text-xs font-medium tracking-[0.2em] uppercase text-[var(--color-text-primary)] hover:text-[var(--color-accent-gold)] transition-all duration-300 focus-ring rounded-sm group"
            >
              <span className="relative z-10">RAYCHARD HUANG</span>
              <span
                className="absolute inset-0 -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-lg"
                style={{
                  background:
                    "radial-gradient(circle, var(--color-accent-gold) 0%, transparent 70%)",
                }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8 lg:gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="relative text-sm font-medium tracking-wide text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-all duration-300 group focus-ring rounded-sm py-1"
                >
                  <span className="relative">
                    {link.label}
                    {/* Underline that draws from left */}
                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-[var(--color-accent-gold)] transition-all duration-300 ease-out group-hover:w-full" />
                    {/* Subtle color fill on hover */}
                    <span className="absolute inset-0 -z-10 bg-[var(--color-accent-gold)]/5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 -mx-2 px-2" />
                  </span>
                </Link>
              ))}
              <ThemeToggle />
              <LanguageToggle />
              <Link
                href="#contact"
                className="relative text-xs font-medium tracking-[0.15em] uppercase text-[var(--btn-primary-text)] bg-[var(--btn-primary-bg)] px-6 py-3 rounded-full hover:bg-[var(--btn-primary-hover-bg)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 focus-ring overflow-hidden group"
              >
                <span className="relative z-10">{t("nav.letsTalk")}</span>
                {/* Glow effect on hover */}
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    boxShadow:
                      "0 6px 24px rgba(201, 162, 39, 0.4), 0 2px 8px rgba(201, 162, 39, 0.2)",
                  }}
                />
              </Link>
            </div>

            {/* Mobile menu button with morph animation */}
            <div className="md:hidden flex items-center gap-2">
              <ThemeToggle />
              <LanguageToggle />
              <button
                className="relative p-3 -mr-2 text-[var(--color-text-primary)] hover:text-[var(--color-accent-gold)] transition-all duration-300 active:scale-95 focus-ring rounded-lg"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <motion.div
                  animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </motion.div>
              </button>
            </div>
          </div>
        </nav>

        {/* Subtle gradient line under nav */}
        <div className="flex justify-center w-full">
          <div
            className="w-[60%] h-px"
            style={{
              background:
                "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.1) 15%, rgba(255, 255, 255, 0.1) 85%, transparent 100%)",
            }}
          />
        </div>
      </motion.header>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-[var(--color-bg-primary)]/98 backdrop-blur-md"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="relative h-full flex flex-col items-center justify-center gap-8 px-6">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.href}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  delay: index * 0.1 + 0.1,
                  duration: 0.5,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Link
                  href={link.href}
                  className="relative text-2xl font-medium text-[var(--color-text-primary)] hover:text-[var(--color-accent-gold)] transition-all duration-300 group focus-ring rounded-sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="relative inline-block">
                    {link.label}
                    {/* Animated underline */}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[var(--color-accent-gold)] to-[var(--color-accent-teal)] transition-all duration-500 ease-out group-hover:w-full" />
                  </span>
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                delay: navLinks.length * 0.1 + 0.15,
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <Link
                href="#contact"
                className="relative text-sm font-medium tracking-[0.15em] uppercase text-[var(--btn-primary-text)] bg-[var(--btn-primary-bg)] px-8 py-4 rounded-full hover:bg-[var(--btn-primary-hover-bg)] hover:scale-105 active:scale-95 transition-all duration-300 focus-ring overflow-hidden group"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="relative z-10">{t("nav.letsTalk")}</span>
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    boxShadow: "0 8px 32px rgba(201, 162, 39, 0.5)",
                  }}
                />
              </Link>
            </motion.div>
          </div>
        </div>
      )}
    </>
  )
}
